/*
 * File: UpdateStudentPermitDialog.tsx
 * Author: Austin Robinson
 * Date Created: 12/06/2025
 * Copyright (c) DrivingDoc 2025
 */
import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DialogProps, useDialogs } from '@toolpad/core/useDialogs';
import { FormProvider, useForm } from 'react-hook-form';

import { getStudentOptions, getStudentQueryKey, updateStudentPermitMutation } from '@drivingdoc/api-client/generated/react-query';
import { GetStudentResponse } from 'types/students';

import DateInput from 'components/FormInputs/DateInput';
import TextInput from 'components/FormInputs/TextInput';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

import FeedbackDialog from '../../FeedbackDialog';
import { UpdateStudentPermitFormValues, UpdateStudentPermitSchema } from './UpdateStudentPermitSchema';

interface UpdateStudentPermitDialogPayload {
  id: string;
}

function UpdateStudentPermitDialog({ payload, open, onClose }: DialogProps<UpdateStudentPermitDialogPayload>) {
  const { id } = payload;
  const dialogs = useDialogs();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Fetch student data
  const student = useQuery({
    ...getStudentOptions({
      path: { id },
    }),
    gcTime: 0,
    enabled: !!id,
  });
  const studentData = student.data as GetStudentResponse | undefined;

  const updatePermitMutation = useMutation(updateStudentPermitMutation());

  // Initialize form
  const form = useForm<UpdateStudentPermitFormValues>({
    mode: 'all',
    resolver: zodResolver(UpdateStudentPermitSchema),
  });

  const { handleSubmit, reset } = form;

  // Populate form with existing permit data
  useEffect(() => {
    if (student.isSuccess && studentData) {
      reset({
        permitNum: studentData.student?.permit_num ?? undefined,
        permitIssued: studentData.student?.permit_issued ?? undefined,
        permitExpiration: studentData.student?.permit_expiration ?? undefined,
        // Future use:
        // permitRestrictions: student.data.student?.permit_restrictions ?? undefined,
        // permitEndorsements: student.data.student?.permit_endorsements ?? undefined,
      });
    }
  }, [student.isSuccess, studentData, reset]);

  // Handle clear all permit info
  const handleClearPermit = async () => {
    try {
      const confirmed = await dialogs.confirm(
        'Are you sure you want to clear all permit information? This action cannot be undone.',
        { okText: 'Clear All', cancelText: 'Cancel', severity: 'error' },
      );

      if (!confirmed) return;

      await updatePermitMutation.mutateAsync({
        path: {
          id,
        },
        body: {
          permitNum: null,
          permitIssued: null,
          permitExpiration: null,
          permitRestrictions: null,
          permitEndorsements: null,
        },
      });

      await queryClient.invalidateQueries({
        queryKey: getStudentQueryKey({ path: { id } }),
        exact: false,
      });
      await onClose();

      dialogs.open(FeedbackDialog, {
        icon: 'success',
        title: 'Permit Information Cleared',
      });
    } catch (error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error clearing permit information',
        message: getApiErrorMessage(error),
      });
    }
  };

  // Handle form submission
  const onSubmit = async (data: UpdateStudentPermitFormValues) => {
    try {
      const confirmed = await dialogs.confirm('Are you sure you want to update the student permit information?', {
        okText: 'Confirm',
        cancelText: 'Cancel',
      });

      if (!confirmed) return;

      await updatePermitMutation.mutateAsync({
        path: {
          id,
        },
        body: data,
      });
      await queryClient.invalidateQueries({
        queryKey: getStudentQueryKey({ path: { id } }),
        exact: false,
      });
      await onClose();

      dialogs.open(FeedbackDialog, {
        icon: 'success',
        title: 'Permit Information Updated',
      });
    } catch (error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error updating permit information',
        message: getApiErrorMessage(error),
      });
    }
  };

  return (
    <Dialog fullScreen={fullScreen} fullWidth maxWidth="sm" open={open} onClose={() => onClose()}>
      <DialogTitle>Update Student Permit Information</DialogTitle>
      <form noValidate onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <FormProvider {...form}>
          <DialogContent>
            <Grid container rowSpacing={3} columnSpacing={2} pt={1}>
              <Grid size={12}>
                <TextInput name="permitNum" label="Permit Number" />
              </Grid>

              <Grid size={6}>
                <DateInput name="permitIssued" label="Permit Issued Date" disableFuture />
              </Grid>

              <Grid size={6}>
                <DateInput name="permitExpiration" label="Permit Expiration Date" disablePast />
              </Grid>

              {/* Future use: */}
              {/* <Grid size={12}>
                <TextInput name="permitRestrictions" label="Permit Restrictions" multiline rows={2} />
              </Grid>

              <Grid size={12}>
                <TextInput name="permitEndorsements" label="Permit Endorsements" multiline rows={2} />
              </Grid> */}
            </Grid>
          </DialogContent>
          <DialogActions
            sx={{
              flexWrap: 'wrap',
              gap: 1,
              px: 2,
              py: 1.5,
            }}
          >
            <Button
              onClick={handleClearPermit}
              color="error"
              variant="outlined"
              disabled={updatePermitMutation.isPending}
            >
              Clear All
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={() => onClose()} variant="outlined">
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={updatePermitMutation.isPending}>
              Update
            </Button>
          </DialogActions>
        </FormProvider>
      </form>
      <Backdrop open={student.isLoading || updatePermitMutation.isPending} sx={{ color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
}

export default UpdateStudentPermitDialog;
